<script lang="ts">
  import { type Snippet } from 'svelte';

  import { ConfirmActions, TextArea, Title, Dropdown, Form } from '$shared/components';
  import CompleteStateInput from './partials/CompleteStateInput.svelte';
  import CancelledStateInput from './partials/CancelledStateInput.svelte';
  import { CredReqEventStatusExtension } from '$shared/models/extensions/CredReqEventStatusExtension';
  import { CvoStatusUpdateFormModel } from '$shared/models/forms/CvoStatusUpdateFormModel';
  import type { CommonList } from '$shared/models/CommonList';
  import ModalEvent from '$shared/constants/ModalEvent';
  import type { XFile } from '$shared/models/XFile';
  import Label from '$shared/constants/Label';
  const StatusList = CredReqEventStatusExtension.getAsList(
    CvoStatusUpdateFormModel.getIngoredCredStatuses()
  );

  interface Props {
    loading?: boolean;
    error?: string | null;
    attachLoading?: boolean;
    files?: XFile[];
    formData: CvoStatusUpdateFormModel;
    completeState?: Snippet | undefined;
    cancelState?: Snippet | undefined;
    onevent?: (payload: any) => void;
  }

  let {
    loading = false,
    error = $bindable(null),
    attachLoading = false,
    files = [],
    formData = $bindable(),
    completeState = undefined,
    cancelState = undefined,
    onevent
  }: Props = $props();

  const isValid = (): boolean => {
    try {
      formData.validate();
      return true;
    } catch (err) {
      error = (err as Error).message;
      return false;
    }
  };

  const handleCancel = () => {
    onevent?.({ type: ModalEvent.close });
  };

  const dispatchCompleteStatusSelectedEvent = (): void => {
    onevent?.({ type: 'complete-selected' });
  };

  const dispatchCompleteStatusUnSelectedEvent = (): void => {
    onevent?.({ type: 'complete-unselected' });
  };

  const handleOk = () => {
    if (!isValid()) return;
    onevent?.({ type: ModalEvent.ok, data: formData });
  };

  const onChangeNote = (e: any): void => {
    formData.setNote(e.target.value);
  };

  const onChangeStatus = (detail: CommonList): void => {
    formData.isCompletedStatus && dispatchCompleteStatusUnSelectedEvent();
    formData.setStatus(detail);
    formData = formData;
    formData.isCompletedStatus && dispatchCompleteStatusSelectedEvent();
  };

  const onClickClearError = (e: any): void => {
    error = null;
  };
</script>

<div class="cvo-resolve-issues-popup" class:scroll={formData.showNote}>
  <div data-cy="cvo-popup-title" class="title">
    <Title type="h2">Update CVO Status</Title>
  </div>
  <Form {error} onalertclose={onClickClearError}>
    <div class="list-input">
      <Dropdown
        name="cvo-popup-dropdown"
        maxWidth
        label={formData.selectedStatusLabel}
        list={StatusList}
        onchange={onChangeStatus}
      />
    </div>
    <div class="note-input" class:show={formData.showNote}>
      <TextArea
        name="cvo-popup-note"
        placeholder="Add message (Optional)"
        value={formData.note}
        oninput={onChangeNote}
      />
      {#if formData.isCompletedStatus}
        <CompleteStateInput loading={attachLoading} {files} {onevent}>
          {@render completeState?.()}
        </CompleteStateInput>
      {/if}
    </div>
    <div class="cancel-check" class:show={formData.isCancelledStatus}>
      <CancelledStateInput {formData} disabled={formData.hasNoMonitorEnabled}>
        {@render cancelState?.()}
      </CancelledStateInput>
    </div>
    <div class="actions">
      <ConfirmActions {loading} okLabel={Label.Submit} onok={handleOk} oncancel={handleCancel} />
    </div>
  </Form>
</div>

<style>
  .cvo-resolve-issues-popup {
    padding: 24px;
    width: 400px;
    overflow-y: none;
    max-height: calc(75vh - 50px);
  }
  .cvo-resolve-issues-popup.scroll {
    overflow-y: auto;
  }
  .title {
    margin-bottom: 16px;
  }
  .list-input {
    min-width: 200px;
    margin-bottom: 24px;
  }
  .actions {
    margin-top: 24px;
  }
  .note-input {
    display: none;
  }
  .note-input.show {
    display: block;
  }
  .cancel-check {
    margin-top: 8px;
    display: none;
  }
  .cancel-check.show {
    display: block;
  }
</style>
